import { useMutation } from '@apollo/client'
import { useEffect } from 'react'

import logger from '@/utils/logger'

import { useUser } from '@/client/context/user'
import { useIsPageVisible } from '@/client/hooks'
import useAsyncDebouncedCallback from '@/client/hooks/useAsyncDebouncedCallback'
import { UPDATE_CONVERSATION_LAST_READ_AT_MUTATION } from '@/client/services/hasura'
import type {
  UpdateConversationLastReadAtMutation,
  UpdateConversationLastReadAtMutationVariables,
} from '@/client/services/hasura/generated/graphql'

const THROTTLE_MILLISECONDS = 1000
export default function useUpdateConversationLastReadAt({
  conversationId,
  latestMessageCreatedAt,
}: {
  conversationId: number | undefined
  latestMessageCreatedAt?: Date | null
}): void {
  const user = useUser()
  const isPageVisible = useIsPageVisible()

  const [updateConversationLastReadAtMutation] = useMutation<
    UpdateConversationLastReadAtMutation,
    UpdateConversationLastReadAtMutationVariables
  >(UPDATE_CONVERSATION_LAST_READ_AT_MUTATION)

  const updateConversationLastReadAt = useAsyncDebouncedCallback(
    async () => {
      if (!isPageVisible) {
        return
      }
      if (conversationId == null) {
        return
      }
      const response = await updateConversationLastReadAtMutation({
        variables: {
          conversationId,
          userId: user.id,
          now: new Date().toISOString(),
        },
      })
      if (response.errors != null && response.errors.length > 0) {
        throw new Error(`Hasura error: ${response.errors.map(item => item.message).join(', ')}`)
      }
    },
    {
      delayMs: THROTTLE_MILLISECONDS,
      immediate: true,
    }
  )

  useEffect(() => {
    updateConversationLastReadAt().catch(err => {
      logger.warning(`Error updating conversation last read at for User#${user.id}`, { err })
    })
  }, [user.id, latestMessageCreatedAt, updateConversationLastReadAt])
}
